import { Truck, Download, ExternalLink } from 'lucide-react';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import { formatCurrency, formatDate } from '@/lib/utils';
import type { Shipment } from './types';

interface ShipmentsCardProps {
  shipments: Shipment[];
  trackingNumber: string | null;
  trackingCarrier: string | null;
  onDownloadLabel: (shipmentId: string) => void;
}

const getShipmentStatusVariant = (status: string): 'green' | 'yellow' | 'blue' | 'gray' => {
  switch (status) {
    case 'delivered': return 'green';
    case 'in_transit': return 'blue';
    case 'label_created': return 'yellow';
    default: return 'gray';
  }
};

export default function ShipmentsCard({ shipments, trackingNumber, trackingCarrier, onDownloadLabel }: ShipmentsCardProps) {
  const hasShipments = shipments && shipments.length > 0;
  const hasLegacyTracking = trackingNumber && !hasShipments;

  if (!hasShipments && !hasLegacyTracking) {
    return null;
  }

  return (
    <div className="space-y-4">
      {hasShipments && shipments.map((shipment) => (
        <Card key={shipment.id} padding="md">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-stone-500" />
              <Badge variant="blue">{shipment.carrier.toUpperCase()}</Badge>
              <span className="text-sm text-stone-700">{shipment.service_name}</span>
            </div>
            <Badge variant={getShipmentStatusVariant(shipment.status)}>
              {shipment.status.replace(/_/g, ' ')}
            </Badge>
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-stone-500">Tracking:</span>
              {shipment.tracking_url ? (
                <a
                  href={shipment.tracking_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline inline-flex items-center gap-1"
                >
                  {shipment.tracking_number}
                  <ExternalLink className="h-3 w-3" />
                </a>
              ) : (
                <span className="text-stone-700">{shipment.tracking_number}</span>
              )}
            </div>

            {shipment.shipped_at && (
              <div>
                <span className="text-stone-500">Shipped:</span>{' '}
                <span className="text-stone-700">{formatDate(shipment.shipped_at)}</span>
              </div>
            )}

            {shipment.delivered_at && (
              <div>
                <span className="text-stone-500">Delivered:</span>{' '}
                <span className="text-stone-700">{formatDate(shipment.delivered_at)}</span>
              </div>
            )}

            {shipment.label_cost > 0 && (
              <div>
                <span className="text-stone-500">Label cost:</span>{' '}
                <span className="text-stone-700">{formatCurrency(shipment.label_cost)}</span>
              </div>
            )}

            {shipment.items && shipment.items.length > 0 && (
              <div className="mt-2 pt-2 border-t border-stone-100">
                <p className="text-stone-500 mb-1">Items:</p>
                <ul className="space-y-1">
                  {shipment.items.map((item, idx) => (
                    <li key={idx} className="text-stone-700">
                      {item.product_name}
                      {item.variant_name && ` - ${item.variant_name}`}
                      {' '}&times; {item.quantity}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {shipment.label_url && (
            <div className="mt-3 pt-3 border-t border-stone-100">
              <Button variant="ghost" size="sm" onClick={() => onDownloadLabel(shipment.id)}>
                <Download className="h-4 w-4 mr-1" />
                Download Label
              </Button>
            </div>
          )}
        </Card>
      ))}

      {hasLegacyTracking && (
        <Card title="Legacy Tracking" padding="md">
          <div className="text-sm space-y-1">
            {trackingCarrier && (
              <div>
                <span className="text-stone-500">Carrier:</span>{' '}
                <span className="text-stone-700">{trackingCarrier.toUpperCase()}</span>
              </div>
            )}
            <div>
              <span className="text-stone-500">Tracking Number:</span>{' '}
              <span className="text-stone-700">{trackingNumber}</span>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
